/**
 * 达人订单详情页 - 工具函数 & 类型定义
 */

import type { StaffOrderDetail } from "@/api/staff";

/** 售后/开票互斥状态 */
export type AfterSaleState = "none" | "refund" | "invoice";

/** 步骤状态类型 */
export type StepStatus = "finish" | "process" | "wait";

/** 达人订单服务进度（6步：待支付→待商家确认→合同签约→待出行→待服务→服务完成） */
export function getServiceSteps(
  status: number,
  _isDepart?: number,
): { label: string; status: StepStatus }[] {
  // 终态：已取消/已退款 — 全部灰显
  if (status === -1 || status === -20) {
    return [
      { label: "待支付", status: "wait" },
      { label: "待商家确认", status: "wait" },
      { label: "合同签约", status: "wait" },
      { label: "待出行", status: "wait" },
      { label: "待服务", status: "wait" },
      { label: "服务完成", status: "wait" },
    ];
  }

  const steps: { label: string; status: StepStatus }[] = [
    { label: "待支付", status: "wait" },
    { label: "待商家确认", status: "wait" },
    { label: "合同签约", status: "wait" },
    { label: "待出行", status: "wait" },
    { label: "待服务", status: "wait" },
    { label: "服务完成", status: "wait" },
  ];

  // status → 当前高亮步骤索引（到达该状态时之前的步骤全部亮起）
  let currentIdx = 0;
  if (status === 0) currentIdx = 0;            // 待付款 → 待支付
  else if (status === 10) currentIdx = 1;       // 待接单 → 待商家确认
  else if (status === 13) currentIdx = 2;       // 待签约 → 合同签约
  else if (status === 16) currentIdx = 3;       // 待出发 → 待出行
  else if (status === 20 || status === 30) currentIdx = 4; // 待服务/进行中 → 待服务
  else if (status >= 40 || status === -10) currentIdx = 5; // 待评价/已完成/退款申请 → 服务完成

  for (let i = 0; i < steps.length; i++) {
    if (i < currentIdx) steps[i].status = "finish";
    else if (i === currentIdx) steps[i].status = "process";
    else steps[i].status = "wait";
  }
  return steps;
}

/** 状态文字映射（右上角标签） */
export function getStatusText(status: number): string {
  const map: Record<number, string> = {
    [-20]: "已退款",
    [-10]: "退款申请",
    [-1]: "已取消",
    0: "待付款",
    10: "待接单",
    13: "待签约",
    16: "待出发",
    20: "待服务",
    30: "进行中",
    40: "待评价",
    50: "已完成",
  };
  return map[status] ?? "未知";
}

/** 是否显示提醒条 */
export function getStatusAlert(
  status: number,
): { visible: boolean; text: string; type: "warning" | "info" | "success" } | null {
  if (status === 0)
    return { visible: true, text: "订单待支付，请尽快完成支付", type: "warning" };
  if (status === 10 || status === 13)
    return { visible: true, text: "商家正在审核，预计1-2小时内确认", type: "warning" };
  if (status === 16)
    return { visible: true, text: "已确认，请按时出行", type: "info" };
  if (status === 20 || status === 30)
    return { visible: true, text: "服务进行中", type: "info" };
  if (status === 40)
    return { visible: true, text: "服务已完成，期待您的评价", type: "success" };
  if (status === 50)
    return { visible: true, text: "订单已完成", type: "success" };
  return null;
}
